import { APIError } from 'helpers/Rest';
import { appUpdateRequired } from 'helpers/project-schema';
import { currentProjectIDSelector, projectByIdSelector } from 'selectors/projectSelector';

import ServerActions from './ServerActions';

export const getProjects = () => async (dispatch, _getState, {api}) => {
  try {
    const projectList = await api.getProjects();
    dispatch(ServerActions.receivedProjectList(projectList));
  } catch(e) {
    if (e instanceof APIError) {
      console.warn(e);
      const {body} = e?.response ?? {};
      const errors = _.get(body, 'errors', body);
      return {errors};
    } else {
      throw e;
    }
  }
};

export const addProject = (project) => async (dispatch, _getState, {api}) => {
  try {
    const addedProject = await api.addProject(project, project.createdOfflineAt);
    dispatch(ServerActions.receivedProject(addedProject, project.id));
    return addedProject;
  } catch(e) {
    if (e instanceof APIError) {
      const {body} = e?.response ?? {};
      const errors = _.get(body, 'errors', body);
      return dispatch(ServerActions.createProjectOffline({...project, errors}));
    } else {
      throw e;
    }
  }
}

export const getProject = (projectId) => async (dispatch, getState, {api, repository}) => {
  try {
    const project = await api.getProject(projectId);
    dispatch(ServerActions.receivedProject(project));
    return project;
  } catch (e) {
    if (e instanceof APIError) {
      const {body} = e?.response ?? {};
      const errors = _.get(body, 'errors', body);
      const project = projectByIdSelector(projectId)(getState());
      return {...project, errors};
    } else {
      throw e;
    }
  }
}

export const updateProject = (project) => async (dispatch, _getState, {api, repository}) => {
  if (project.createdOfflineAt) return _.noop;
  if (appUpdateRequired(project)) return _.noop;
  try {
    const updatedProject = await api.updateProject(project);
    dispatch(ServerActions.receivedProject(updatedProject));
    return updatedProject;
  } catch(e) {
    if (e instanceof APIError) {
      const {body, statusCode} = e?.response ?? {};
      const errors = _.get(body, 'errors', body);
      if (statusCode === 0) {
        const updatedProject = await dispatch(ServerActions.updateProjectOffline(project, errors));
        return {...updatedProject, errors};
      }

      return {...project, errors};
    } else {
      throw e;
    }
  }
}

export const deleteProject = (projectId) => async (dispatch, getState, {api}) => {
  const project = _.find(getState().projects, {id: projectId});

  if (project) {
    if (project.createdOfflineAt) {
      dispatch({type: 'DELETED_PROJECT', project});
      return {...project, errors: null};
    } else {
      try {
        const projectList = await api.deleteProject(projectId);
        await dispatch(ServerActions.receivedProjectList(projectList, projectId));
        return project;
      } catch(e) {
        if (e instanceof APIError) {
          const {body, statusCode} = e?.response ?? {};
          const errors = _.get(body, 'errors', body);
          if (statusCode === 0) {
            await dispatch(ServerActions.deleteProjectOffline(projectId, errors, statusCode));
          }
          return {...project, errors};
        } else {
          throw e;
        }
      }
    }
  } else {
    return {errors: `Project was not found with ID: ${projectId}`};
  }
}

export const unsetCurrentProject = () => ({type: 'UNSET_CURRENT_PROJECT'});
export const setCurrentProject = (projectId) => async (dispatch, getState) => {
  if (currentProjectIDSelector(getState()) !== projectId) {
    dispatch({type: 'SET_CURRENT_PROJECT', id: projectId});
  }

  const project = projectByIdSelector(projectId)(getState());
  if (!project) {
    return await dispatch(getProject(projectId));
  }

  return project;
}

export const updateProjectSettings = (projectData, project) => {
  return async (dispatch, _getState) => {
    if (project.createdOfflineAt) {
      const updatedProject = {...project, ...projectData};
      dispatch(ServerActions.receivedProject(updatedProject));
      return await dispatch(addProject(updatedProject));
    } else {
      return await dispatch(updateProject(projectData));
    }
  };
}

export const getPublicProjects = () => async (dispatch, _getState, {api}) => {
  try {
    const publicProjectList = await api.getPublicProjects();
    dispatch(ServerActions.receivedPublicProjectList(publicProjectList));
  }  catch(e) {
    if (e instanceof APIError) {
      console.warn(e);
    } else {
      throw e;
    }
  }
}

export function silentUpdateProject(project) {
  return async (dispatch, _getState, {api}) => {
    if (project.createdOfflineAt) return;
    try {
      await api.updateProject(project);
    } catch (e) {
      if (e instanceof APIError) {
        if (e?.response?.statusCode === 0) {
          dispatch(ServerActions.updateProjectOffline(project));
        }
      } else {
        throw e;
      }
    }
  };
}

export function downloadReport({id, markup, cut_sheet_pages, callback, errorCallback}) {
  return async (_dispatch, _getState, {api}) => {
    let report;
    try {
      report = await api.downloadReport({id, markup, cut_sheet_pages});
    }  catch(e) {
      try {
        if (errorCallback) {
          errorCallback(e);
          return;
        }
      } catch(e) {
        console.warn("error in errorCallback:", e);
      } finally {
        if (e instanceof APIError) {
          console.warn(e);
        } else {
          throw e;
        }
      }
    }

    if (callback && report) callback(report);
    return report;
  };
}

export function downloadLogsReport({id, markup}) {
  return async (_dispatch, _getState, {api}) => {
    return await api.downloadLogsReport({id, markup});
  };
}

export default {
  getProjects,
  addProject,
  getProject,
  updateProject,
  deleteProject,
  unsetCurrentProject,
  setCurrentProject,
  updateProjectSettings,
  getPublicProjects,

  silentUpdateProject,
  downloadReport,
  downloadLogsReport,
};
